import { type RefObject, useCallback, useLayoutEffect, useRef, useState } from "react";
import { placePopover, type PlacementPreference, type PopoverPosition } from "./popoverPlacement";

interface PopoverPlacement {
  panelRef: RefObject<HTMLDivElement | null>;
  /** `null` until measured after opening, so the panel can stay hidden until it is placed. */
  position: PopoverPosition | null;
  place: () => void;
}

/** Measures the anchor and the panel and keeps the panel's position while it is open. */
export function usePopoverPlacement(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  { align, side }: PlacementPreference,
): PopoverPlacement {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    // The full content height, not the shown one: a panel cut to fit must be able to grow back.
    const size = { width: panel.getBoundingClientRect().width, height: panel.scrollHeight };
    const geometry = { anchor: anchor.getBoundingClientRect(), panel: size, viewport };
    setPosition(placePopover(geometry, { align, side }));
  }, [anchorRef, align, side]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    place();
  }, [open, place]);

  return { panelRef, position, place };
}
